import { ds } from "js/core/models/dataService";
import { NodeType } from "common/constants";
import { _, $ } from "js/vendor";
import { getStaticUrl } from "js/config";
import { controls } from "js/editor/ui";
import renderReactRoot from "js/react/renderReactRoot";
import getLogger, { LogGroup } from "js/core/logger";
import * as geom from "js/core/utilities/geom";

import { ElementOptionsMenu, ElementRollover } from "../BaseElementEditor";
import { CollectionElementSelection } from "../CollectionElementEditor";
import { ImageFrameMenu } from "../EditorComponents/ImageFrameMenu";
import { ShowWarningDialog } from "js/react/components/Dialogs/BaseDialog";

const logger = getLogger(LogGroup.ELEMENTS);

const TimelineSelection = CollectionElementSelection.extend({
    renderControls: function() {
        this.addMilestonePromiseChain = Promise.resolve();

        this.addControl({
            type: controls.BUTTON,
            label: "Add Milestone",
            icon: "add_circle",
            enabled: this.element.annotations.itemCount < this.element.annotations.maxItemCount,
            callback: () => {
                this.addMilestonePromiseChain = this.addMilestonePromiseChain
                    .then(async () => {
                        const annotations = this.element.annotations;

                        const annotationModel = this.element.getDefaultAnnotationModel();
                        annotationModel.x = annotations.model.items.reduce((x, itemModel) => Math.max(x, itemModel.x), 0);
                        annotationModel.x = (1 - annotationModel.x) / 2 + annotationModel.x;

                        const annotation = annotations.addItem(annotationModel);

                        await this.element.canvas.updateCanvasModel(true);

                        ds.selection.element = annotations.getItemElementById(annotation.id);
                    })
                    .catch(err => logger.error(err, "[TimelineSelection] failed to add milestone", { slideId: this.element.canvas.dataModel?.id }));
            }
        });

        this.addControl({
            id: "nodeType",
            type: controls.POPUP_BUTTON,
            icon: "apps",
            showArrow: false,
            menuClass: "icon-menu fivecol",
            items: [{
                label: "Text",
                value: NodeType.TEXT,
                image: getStaticUrl("/images/ui/node_types/node_text.svg")
            }, {
                label: "Bullet",
                value: NodeType.BULLET_TEXT,
                image: getStaticUrl("/images/ui/node_types/node_bullet_text.svg")
            }, {
                label: "Numbered",
                value: NodeType.NUMBERED_TEXT,
                image: getStaticUrl("/images/ui/node_types/node_numbered_text.svg")
            }, {
                label: "Lettered",
                value: NodeType.LETTERED_TEXT,
                image: getStaticUrl("/images/ui/node_types/node_lettered_text.svg")
            }, {
                label: "Media with Text",
                value: NodeType.CONTENT_AND_TEXT,
                image: getStaticUrl("/images/ui/node_types/node_content_and_text.svg")
            }],
            callback: value => {
                ds.selection.element = null;
                this.element.model.annotations.items.forEach(itemModel => itemModel.nodeType = value);
                this.element.canvas.updateCanvasModel(true);
            }
        });

        const contentAndTextElement = this.element.annotations.itemElements.find(element => element?.nodeType === NodeType.CONTENT_AND_TEXT);
        if (contentAndTextElement) {
            this.addControl({
                type: controls.POPUP_BUTTON,
                icon: "filter_frames",
                showArrow: false,
                customMenuClass: "frame-popup",
                menuContents: closeMenu => {
                    const $menu = $.div();
                    renderReactRoot(
                        ImageFrameMenu,
                        {
                            onSelect: frame => {
                                this.element.annotations.itemElements.forEach(annotation => {
                                    annotation.model.frameType = frame;
                                    annotation.markStylesAsDirty();
                                });
                                this.element.canvas.updateCanvasModel(false);
                                closeMenu();
                            },
                            allowedCategories: contentAndTextElement.options.allowedFrameCategories
                        },
                        $menu[0]
                    );
                    return $menu;
                },
                transitionModel: false
            });
        }

        this.addControl({
            type: controls.BUTTON,
            label: "Auto Arrange",
            icon: "view_column",
            callback: () => {
                (async () => {
                    await this.element.distributeMilestones();
                    await this.element.canvas.updateCanvasModel(true);
                })();
            }
        });
    }
});

const TimelineRollover = ElementRollover.extend({

    captureMouseEvents: true,

    renderControls: function() {
        const $addMilestoneButton = this.$el.addEl($.div("ui_widget").css("pointer-events", "none"));
        $addMilestoneButton.append($.div("add_component_button button", "Add Milestone").left(-55).top(-40));
        $addMilestoneButton.top(this.elementBounds.height / 2).width(120);
        $addMilestoneButton.append($.div("vertical_line").left(0).top(-30));
        $addMilestoneButton.append($.div("add_item_button button").left(-10).top(0));

        const timelineBounds = this.element.timeline.calculatedProps.bounds;
        $addMilestoneButton.on("click", event => {
            const x = this.screenToCanvasCoordinates(new geom.Point(event.pageX, event.pageY)).x - this.element.bounds.left;
            // convert x to percentage
            const percentX = (x - timelineBounds.left) / timelineBounds.width;
            this.addItem(percentX);
        });

        this.registerSubComponent({
            widget: $addMilestoneButton,
            bounds: this.canvasToSelectionCoordinates(new geom.Rect(timelineBounds.left, timelineBounds.height / 2 - 15, timelineBounds.width, 30)),
            rollover: event => {
                $addMilestoneButton.left(event.pageX - this.$el.offset().left).top(this.elementBounds.height / 2 - 10);
            },
        });

        // these widget subcomponents exist just to hide the $addMilestoneButton widget when rolling over a milestone point
        for (const item of this.element.annotations.itemElements) {
            const $milestoneDrag = this.$el.addEl($.div("ui_widget"));
            this.registerSubComponent({
                widget: $milestoneDrag,
                target: item,
                bounds: new geom.Rect(this.canvasToSelectionCoordinates(item.bounds).left - 15, this.elementBounds.height / 2 - 15, 30, 30),
                rollover: event => {
                    $addMilestoneButton.hide();
                }
            });
        }
    },

    addItem: function(x) {
        const annotationModel = this.element.getDefaultAnnotationModel();
        annotationModel.x = x;
        this.element.annotations.addItem(annotationModel);
        this.element.canvas.updateCanvasModel(true).catch(err => {
            ShowWarningDialog({
                title: "Unable to add item",
                message: err.message,
            });
        });
    }
});

const TimelineOptionsMenu = ElementOptionsMenu.extend({
    renderControls: function() {
        this.addControl({
            type: controls.TOGGLE,
            label: "Start Marker",
            property: "showStartMarker"
        });

        this.addControl({
            type: controls.TOGGLE,
            label: "End Marker",
            property: "showEndMarker"
        });
    }
});

export const editors = {
    TimelineSelection,
    TimelineRollover,
    TimelineOptionsMenu
};
